import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

import { useRichTextEditor } from '../rich-text-editor-provider';
import type { VariableItem, VariableProps } from '../types/rich-text-editor';

export interface VariableListProps {
	command: (item: { id: string; label?: string; token?: string; prefix?: string }) => void;
	query: string;
	variableProps?: VariableProps;
	/** Hides the popup without touching the typed query. */
	onDismiss?: () => void;
}

const DEFAULT_SEARCH_DEBOUNCE_MS = 200;
const SKELETON_ROW_WIDTHS = ['w-4/5', 'w-3/5', 'w-2/3'];

/** idle → below min length / no async source · loading → fetch in flight · done → resolved. */
type SearchPhase = 'idle' | 'loading' | 'done';

function VariableListSkeleton() {
	return (
		<div className="flex flex-col gap-1.5 px-2 py-1.5">
			{SKELETON_ROW_WIDTHS.map((w) => (
				<div
					key={w}
					className={cn('h-3.5 animate-pulse rounded bg-slate-100', w)}
				/>
			))}
		</div>
	);
}

const VariableList = forwardRef<
	{ onKeyDown: (event: KeyboardEvent) => boolean },
	VariableListProps
>(({ command, query, variableProps: providedVariableProps, onDismiss }, ref) => {
	const { variableProps: contextVariableProps } = useRichTextEditor();
	const variableProps = providedVariableProps ?? contextVariableProps;
	const [selectedIndex, setSelectedIndex] = useState(0);

	const items = useMemo(() => variableProps?.items ?? [], [variableProps?.items]);
	const searchItems = variableProps?.searchItems;
	const minChars = variableProps?.searchMinChars ?? 0;
	const debounceMs = variableProps?.searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS;

	// Classic in-memory filtering (used when there is no async source).
	const staticItems = useMemo(() => {
		const normalized = query.toLowerCase();
		return items
			.filter((variable) => {
				const candidates = [variable.label, variable.token, variable.id].filter(
					Boolean
				) as string[];
				return candidates.some((candidate) => candidate.toLowerCase().includes(normalized));
			})
			.slice(0, 10);
	}, [items, query]);

	const [asyncItems, setAsyncItems] = useState<VariableItem[]>([]);
	const [phase, setPhase] = useState<SearchPhase>('idle');

	// Debounced async fetch. The cancelled flag drops stale resolutions so a slow
	// earlier query can never overwrite a newer one's results.
	useEffect(() => {
		if (!searchItems) return;
		const trimmed = query.trim();
		if (trimmed.length < minChars) {
			setPhase('idle');
			setAsyncItems([]);
			return;
		}

		let cancelled = false;
		setPhase('loading');
		const handle = setTimeout(() => {
			searchItems(trimmed)
				.then((results) => !cancelled && setAsyncItems(results))
				.catch(() => !cancelled && setAsyncItems([]))
				.finally(() => !cancelled && setPhase('done'));
		}, debounceMs);

		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
	}, [searchItems, query, minChars, debounceMs]);

	// An async source replaces the static list entirely — no consumer needs both,
	// so we don't merge (add merging only if one ever does).
	const displayItems = searchItems ? asyncItems : staticItems;

	const footerAction = phase === 'loading' ? undefined : variableProps?.footerAction;
	const rowCount = displayItems.length + (footerAction ? 1 : 0);

	const runFooterAction = () => {
		onDismiss?.();
		footerAction?.onSelect();
	};

	const selectItem = (index: number) => {
		const item = displayItems[index];
		if (item)
			command({
				id: item.id,
				label: item.label,
				token: item.token,
				prefix: item.prefix ?? variableProps?.mentionPrefix,
			});
		else if (footerAction) runFooterAction();
	};

	useEffect(() => setSelectedIndex(0), [displayItems]);

	useImperativeHandle(ref, () => ({
		onKeyDown: (event: KeyboardEvent) => {
			if (!rowCount) return false;
			if (event.key === 'ArrowUp') {
				setSelectedIndex((prev) => (prev + rowCount - 1) % rowCount);
				return true;
			}
			if (event.key === 'ArrowDown') {
				setSelectedIndex((prev) => (prev + 1) % rowCount);
				return true;
			}
			if (event.key === 'Enter') {
				selectItem(selectedIndex);
				event.preventDefault();
				event.stopPropagation();
				return true;
			}
			return false;
		},
	}));

	// Show the popup when there's something in it: results, a loading skeleton, a
	// resolved "no results" line, or a footer action. Otherwise hide it.
	const shouldRender = rowCount > 0 || phase === 'loading' || phase === 'done';
	if (!shouldRender) return null;

	return (
		<div className="flex max-h-64 min-w-[220px] flex-col gap-0.5 overflow-auto rounded-md border border-slate-200 bg-white p-1.5 shadow-md">
			{variableProps?.searchHeader && (
				<div className="px-2 pb-1 pt-0.5 text-[11px] font-medium capitalize tracking-wide text-muted-foreground">
					{variableProps.searchHeader}
				</div>
			)}

			{displayItems.map((item, index) => (
				<button
					key={item.id}
					type="button"
					className={cn(
						'flex w-full flex-col items-start rounded px-2 py-1 text-left text-sm transition-colors',
						index === selectedIndex ? 'bg-blue-50 text-primary-blue' : 'hover:bg-slate-50'
					)}
					onClick={() => selectItem(index)}
				>
					<span className="font-medium text-primary">
						{item.label || item.token || `{${item.id}}`}
					</span>
					{item.description ? (
						<span className="text-[11px] text-muted-foreground">{item.description}</span>
					) : null}
				</button>
			))}

			{phase === 'loading' && <VariableListSkeleton />}

			{phase === 'done' && !displayItems.length && (
				<div className="px-2 py-2 text-xs text-muted-foreground">
					{variableProps?.searchEmptyText ?? 'No results found'}
				</div>
			)}

			{footerAction && (
				<>
					{displayItems.length > 0 && <div className="-mx-1.5 my-1 h-px bg-slate-200" />}
					<button
						type="button"
						className={cn(
							'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-primary transition-colors',
							selectedIndex === displayItems.length ? 'bg-blue-50' : 'hover:bg-slate-50'
						)}
						onMouseDown={(event) => event.preventDefault()}
						onClick={runFooterAction}
					>
						{footerAction.icon}
						{footerAction.label}
					</button>
				</>
			)}
		</div>
	);
});

VariableList.displayName = 'VariableList';

export default VariableList;
